import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon } from 'react-bootstrap-icons';

// The brand's logo and cover pickers, shared by the top-level Profil page and
// the app's full brand profile (BrandDashboard's ProfileTab). Both upload
// through updateBrandProfile (PUT /brands/:id, multipart `files` + `cover`);
// the rules are the same in both places: any image the browser offers
// (accept="image/*"), no crop on our side, stored as logo.jpg / cover.jpg.

/// A picked image file and its preview URL. `initial` is the stored image;
/// `setStored` swaps the preview to the URL the API answered with after a save.
export function useImagePick(initial) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(initial || null);
  const objectUrl = useRef(null);

  const revoke = () => {
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    objectUrl.current = null;
  };
  useEffect(() => revoke, []);

  const pick = useCallback((e) => {
    const f = e.target.files?.[0];
    // The same file picked again still fires change next time.
    e.target.value = '';
    if (!f || (f.type && !f.type.startsWith('image/'))) return;
    revoke();
    objectUrl.current = URL.createObjectURL(f);
    setFile(f);
    setPreview(objectUrl.current);
  }, []);

  const setStored = useCallback((url) => {
    setFile(null);
    if (url) {
      revoke();
      setPreview(url);
    }
  }, []);

  /// Only fills in the stored image when nothing is shown yet.
  const setInitial = useCallback((url) => {
    if (url) setPreview((prev) => prev || url);
  }, []);

  return { file, preview, pick, setStored, setInitial };
}

/// `onPick` null = read-only (no picker).
export function CoverField({ src, onPick, label, hint }) {
  return (
    <div className="cover-field">
      <div className="cover-field__banner">
        {src ? (
          <img src={src} alt="" />
        ) : (
          <div className="cover-field__ph">
            <ImageIcon size={22} />
            <span>{hint}</span>
          </div>
        )}
        {onPick && (
          <label className="cover-field__pick">
            <ImageIcon size={14} style={{ marginRight: 6 }} />
            {label}
            <input type="file" accept="image/*" onChange={onPick} hidden data-testid="cover-input" />
          </label>
        )}
      </div>
    </div>
  );
}

export function LogoField({ src, name, onPick, label, round = false }) {
  return (
    <div className="logo-field">
      <div className={`logo-field__preview${round ? ' logo-field__preview--round' : ''}`}>
        {src ? <img src={src} alt="" /> : <span>{name?.[0]?.toUpperCase() || 'B'}</span>}
      </div>
      {onPick && (
        <label className="logo-field__pick">
          {label}
          <input type="file" accept="image/*" onChange={onPick} hidden data-testid="logo-input" />
        </label>
      )}
    </div>
  );
}
